import { defineStore } from "pinia";
import { useLocalStorage, StorageSerializers } from "@vueuse/core";
import { $http } from "@/plugins/api";
import { getApiClientUrl } from "@/config/runtimeConfig";

import type { Policy } from "./authorisation.types";

const LOCAL_STORAGE_POLICY_KEY = "store_authorisation_policy";

const useAuthorisationStore = defineStore("authorisation", () => {
  const policy = useLocalStorage<Policy | null>(
    LOCAL_STORAGE_POLICY_KEY,
    null,
    {
      serializer: StorageSerializers.object,
    },
  );

  // Tracks the in-flight fetch and whether one has already been attempted, so
  // callers that run on every navigation (the Auth0 guard) issue at most one
  // request per session rather than one per route change. Not reactive —
  // purely dedup handles, reset by `setPolicy` so the next sign-in refetches.
  let inflight: Promise<void> | null = null;
  let attempted = false;

  async function fetchPolicy() {
    try {
      const res = await $http.get<Policy[]>(
        `${getApiClientUrl()}/v1/rbacker/policies`,
      );

      // There is no support of the multiple roles at the moment.
      setPolicy(res.data[0]);
    } catch {
      setPolicy(null);

      throw new Error("useAuthorisationStore error: While fetching policy");
    }
  }

  /**
   * Fetch the policy at most once; reuse the cached result thereafter.
   * Concurrent callers before the first fetch resolves share one request.
   *
   * Unlike `fetchPolicy` this never throws and never retries on every call —
   * it is called from the router guard, where a rejection would strand
   * navigation and a per-navigation retry would hammer the endpoint.
   */
  async function ensurePolicy(): Promise<Policy | null> {
    if (policy.value) return policy.value;
    if (attempted) return null;

    if (!inflight) {
      inflight = fetchPolicy()
        .catch(() => {})
        .finally(() => {
          attempted = true;
          inflight = null;
        });
    }
    await inflight;

    return policy.value ?? null;
  }

  function setPolicy(newPolicy: Policy | null) {
    policy.value = newPolicy;
    // A fresh policy (or a cleared cache on sign-out) reopens the gate above.
    attempted = false;
  }

  return { policy, fetchPolicy, ensurePolicy, setPolicy };
});

export { useAuthorisationStore };
